import rough from "roughjs/bin/rough";

import { CaptureUpdateAction, useExcalidrawAPI } from "@excalidraw/excalidraw";
import { useUIAppState } from "@excalidraw/excalidraw/context/ui-appState";
import { renderStaticScene } from "@excalidraw/excalidraw/renderer/staticScene";
import {
  arrayToMap,
  sceneCoordsToViewportCoords,
  THEME,
  toBrandedType,
} from "@excalidraw/common";
import {
  getElementsOverlappingFrame,
  newFrameElement,
  withAnimationOverridesSuspended,
} from "@excalidraw/element";
import { useCallback, useEffect, useReducer, useRef, useState } from "react";

import type {
  ExcalidrawElement,
  ExcalidrawFrameLikeElement,
  NonDeletedSceneElementsMap,
} from "@excalidraw/element/types";
import type { RenderableElementsMap } from "@excalidraw/excalidraw/scene/types";
import type { AppState } from "@excalidraw/excalidraw/types";

import {
  CAMERA_MOVE_LABELS,
  CAMERA_MOVES,
  CAMERA_TRACK_CHANNELS,
  cameraAt,
  getScreenCamera,
  withScreenCamera,
} from "../../animation/camera";
import { downloadBlob } from "../../animation/export/download";
import { layoutClips, layoutElements } from "../../animation/layout";
import { recordScreens } from "../../animation/export/record";
import { animationPresets } from "../../animation/presets";
import { AnimationRuntime } from "../../animation/runtime";
import { duplicateScreen, screenOrderPatches } from "../../animation/screens";
import { buildSequence, sampleSequence } from "../../animation/sequence";
import {
  DEFAULT_TRANSITION_MS,
  getScreenTransition,
  travelEase,
  travelRectAt,
  TRANSITION_TYPES,
  withScreenTransition,
} from "../../animation/transitions";
import {
  getElementAnimations,
  getScreenDurationMs,
} from "../../animation/types";
import { getScreens } from "../../flipbook/ops";
import { AnimationPanel } from "../AnimationPanel";
import { ensureImageCache, sharedImageCache } from "../FlipbookBar";
import { KeyframeEditor } from "../KeyframeEditor";

import "./Studio.scss";

import type { ExportFormat } from "../../animation/export/encoders";
import type { TransitionType, TravelRect } from "../../animation/transitions";

/** Fired by other UI (the Export section's Studio button) to open Studio. */
export const STUDIO_OPEN_EVENT = "chartoon:studio-open";

/** Captured at module load — initializeScene rewrites the URL (replaceState
 * strips unknown params) asynchronously during boot, so reading
 * location.search at component mount was a race the real cloud-board path
 * lost. Import time always precedes the rewrite. */
const STUDIO_REQUESTED_AT_BOOT =
  typeof window !== "undefined" &&
  new URLSearchParams(window.location.search).get("studio") === "1";

const THUMB_H = 72;
const PX_PER_SEC = 72;

/** Clip-lane colors by preset category — the NLE track palette. */
const CATEGORY_COLORS: Record<string, string> = {
  entrance: "#6fbf73",
  exit: "#e05555",
  attention: "#e0c04d",
  text: "#5a8fd6",
  spatial: "#c77dd6",
  physics: "#d98a5f",
  color: "#5ac8b8",
  custom: "#9a9aa0",
  morph: "#4db6ac",
};

const fmtTime = (ms: number) => {
  const s = Math.max(0, ms) / 1000;
  return `${Math.floor(s / 60)}:${(s % 60).toFixed(1).padStart(4, "0")}`;
};

/** One live screen thumbnail — the FlipbookBar recipe at card size. */
const ScreenThumb = ({
  frame,
  version,
}: {
  frame: ExcalidrawFrameLikeElement;
  version: number;
}) => {
  const excalidrawAPI = useExcalidrawAPI();
  const appState = useUIAppState();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const width = Math.max(
    40,
    Math.min(148, Math.round((frame.width / frame.height) * THUMB_H)),
  );

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !excalidrawAPI) {
      return;
    }
    const elements = excalidrawAPI.getSceneElements();
    const zoomValue = Math.min(width / frame.width, THUMB_H / frame.height);
    // thumbs show TRUE content — never the playhead's animated state
    const draw = () =>
      withAnimationOverridesSuspended(() =>
        renderStaticScene(
          {
            canvas,
            rc: rough.canvas(canvas),
            elementsMap: toBrandedType<RenderableElementsMap>(
              arrayToMap(elements),
            ),
            allElementsMap: toBrandedType<NonDeletedSceneElementsMap>(
              arrayToMap(elements),
            ),
            visibleElements: elements,
            scale: window.devicePixelRatio,
            appState: {
              ...appState,
              theme: THEME.LIGHT,
              scrollX: -frame.x,
              scrollY: -frame.y,
              zoom: { value: zoomValue },
              shouldCacheIgnoreZoom: false,
              selectedElementIds: {},
              frameRendering: {
                enabled: true,
                outline: false,
                name: false,
                clip: true,
              },
            } as AppState,
            renderConfig: {
              canvasBackgroundColor: appState.viewBackgroundColor,
              imageCache: sharedImageCache,
              renderGrid: false,
              isExporting: true,
              embedsValidationStatus: new Map(),
              elementsPendingErasure: new Set(),
              pendingFlowchartNodes: null,
              theme: THEME.LIGHT,
            },
          },
          false, // un-throttled: never clobber the main canvas's render slot
        ),
      );
    draw();
    ensureImageCache(elements, excalidrawAPI.getFiles(), draw);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [excalidrawAPI, frame, width, version, appState.viewBackgroundColor]);

  return (
    <canvas
      ref={canvasRef}
      className="studio__thumb"
      width={width * window.devicePixelRatio}
      height={THUMB_H * window.devicePixelRatio}
      style={{ width, height: THUMB_H }}
    />
  );
};

type PopoverState =
  | { kind: "joint"; frameId: string; x: number }
  | { kind: "camera"; frameId: string; x: number }
  | null;

/**
 * Studio mode — the board-scoped sequence editor: screen cards on the left
 * (drag-and-drop order, rename, duplicate/delete), the live canvas center
 * stage, and the sequence timeline along the bottom with a scrubbing
 * playhead, transition joints and camera chips. Opened with ?studio=1 (the
 * Export section's "Open Studio ↗" opens the board in a new tab) or via
 * STUDIO_OPEN_EVENT.
 */
export const StudioMode = () => {
  const excalidrawAPI = useExcalidrawAPI();
  const [open, setOpen] = useState(STUDIO_REQUESTED_AT_BOOT);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [popover, setPopover] = useState<PopoverState>(null);
  const dragFrom = useRef<number | null>(null);
  const [dropIndex, setDropIndex] = useState<number | null>(null);
  const trimRef = useRef<{
    id: string;
    startX: number;
    startMs: number;
  } | null>(null);

  // ---- playhead / transport ----
  const [playheadMs, setPlayheadMs] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [loop, setLoop] = useState(true);
  const [exportFormat, setExportFormat] = useState<ExportFormat>("gif");
  const [exportProgress, setExportProgress] = useState<number | null>(null);
  const loopRef = useRef(loop);
  loopRef.current = loop;
  const playingRef = useRef(false);
  const runtimeRef = useRef<AnimationRuntime | null>(null);
  const compiledForRef = useRef<string | null>(null);
  const tlContentRef = useRef<HTMLDivElement>(null);
  const scrubbingRef = useRef(false);

  const [, forceRender] = useReducer((n: number) => n + 1, 0);
  useEffect(() => {
    if (!excalidrawAPI) {
      return;
    }
    return excalidrawAPI.onChange(() => forceRender());
  }, [excalidrawAPI]);
  useEffect(() => {
    if (!excalidrawAPI) {
      return;
    }
    // the focused-stage masks are positioned from scroll/zoom — repaint on
    // every viewport change
    return excalidrawAPI.onScrollChange(() => forceRender());
  }, [excalidrawAPI]);

  const framedOnOpenRef = useRef(false);
  useEffect(() => {
    if (!open) {
      framedOnOpenRef.current = false;
    }
  }, [open]);

  useEffect(() => {
    const onOpen = () => setOpen(true);
    window.addEventListener(STUDIO_OPEN_EVENT, onOpen);
    return () => window.removeEventListener(STUDIO_OPEN_EVENT, onOpen);
  }, []);

  // distinct editor identity: dim the app's drawing chrome while in Studio
  // (hover restores it — the canvas stays fully usable)
  useEffect(() => {
    document.body.classList.toggle("studio-active", open);
    return () => document.body.classList.remove("studio-active");
  }, [open]);

  const releaseOverrides = useCallback(() => {
    playingRef.current = false;
    setIsPlaying(false);
    runtimeRef.current?.dispose();
    runtimeRef.current = null;
    compiledForRef.current = null;
    excalidrawAPI?.triggerRender(true);
  }, [excalidrawAPI]);

  // closing Studio releases every animation override and stops playback
  useEffect(() => {
    if (!open) {
      releaseOverrides();
    }
  }, [open, releaseOverrides]);
  useEffect(() => releaseOverrides, [releaseOverrides]);

  const captureUndo = useCallback(() => {
    excalidrawAPI?.updateScene({
      elements: excalidrawAPI.getSceneElementsIncludingDeleted(),
      captureUpdate: CaptureUpdateAction.IMMEDIATELY,
    });
  }, [excalidrawAPI]);

  /** Renders the sequence at a global time: screen viewport (camera
   * applied) + element animations at the local time. Transitions show as
   * a cut here except travel, which interpolates natively — full
   * compositing runs in Play sequence and export.
   * ponytail: overlay compositing during scrub needs async snapshots per
   * pointer-move; add if the cut ever feels wrong. */
  const applyTime = useCallback(
    (tMs: number) => {
      if (!excalidrawAPI) {
        return;
      }
      const elements = excalidrawAPI.getSceneElements();
      const screens = getScreens(elements);
      const seq = buildSequence(screens, elements);
      if (seq.entries.length === 0) {
        return;
      }
      const sample = sampleSequence(seq, tMs);
      const entry = seq.entries[sample.index];
      // ponytail: sampleSequence holds localMs at 0 during a travel window,
      // so the incoming screen renders at its t=0 state for the whole glide;
      // per-time sampling of both screens is the upgrade if that ever reads
      // badly
      const travelFrom =
        sample.transition && entry.transitionIn?.type === "travel"
          ? seq.entries[sample.transition.fromIndex].frame
          : null;
      const runtime =
        runtimeRef.current ?? (runtimeRef.current = new AnimationRuntime());
      if (compiledForRef.current !== entry.frame.id) {
        runtime.compile(
          getElementsOverlappingFrame(
            elements,
            entry.frame,
            arrayToMap(elements),
          ),
        );
        compiledForRef.current = entry.frame.id;
      }
      runtime.sampleAt(sample.localMs);
      const st = excalidrawAPI.getAppState();
      const frame = entry.frame;
      const rect: TravelRect = travelFrom
        ? travelRectAt(
            travelFrom,
            frame,
            travelEase(sample.transition!.progress),
          )
        : frame;
      const progress =
        entry.durationMs > 0 ? sample.localMs / entry.durationMs : 0;
      // camera holds neutral while traveling — it belongs to the dwell
      const cam =
        entry.camera && !travelFrom
          ? cameraAt(entry.camera, progress)
          : { dx: 0, dy: 0, zoom: 1 };
      // center within the USABLE stage — the area the rail (left), topbar
      // and timeline don't cover — so the framed screen never hides behind
      // Studio chrome
      const PAD_LEFT = 216;
      const PAD_RIGHT = 336; // inspector panel
      const PAD_TOP = 64;
      const PAD_BOTTOM = 232;
      const usableW = Math.max(100, st.width - PAD_LEFT - PAD_RIGHT);
      const usableH = Math.max(100, st.height - PAD_TOP - PAD_BOTTOM);
      const fit = Math.min(
        usableW / Math.max(1, rect.width),
        usableH / Math.max(1, rect.height),
      );
      const zoom = Math.min(30, Math.max(0.1, fit * cam.zoom * 0.94));
      const centerX = PAD_LEFT + usableW / 2;
      const centerY = PAD_TOP + usableH / 2;
      excalidrawAPI.updateScene({
        appState: {
          scrollX:
            centerX / zoom - (rect.x + rect.width / 2 + cam.dx * rect.width),
          scrollY:
            centerY / zoom - (rect.y + rect.height / 2 + cam.dy * rect.height),
          zoom: { value: zoom as typeof st.zoom.value },
        },
        captureUpdate: CaptureUpdateAction.NEVER,
      });
      setActiveId(frame.id);
      excalidrawAPI.triggerRender(true);
    },
    [excalidrawAPI],
  );

  const seek = useCallback(
    (tMs: number) => {
      setPlayheadMs(tMs);
      applyTime(tMs);
    },
    [applyTime],
  );

  // frame the first screen as soon as one exists — the stage shows ONE
  // screen from the very first paint, even when the scene hydrates late
  useEffect(() => {
    if (!open || !excalidrawAPI) {
      return;
    }
    const frameFirstScreen = () => {
      if (
        !framedOnOpenRef.current &&
        getScreens(excalidrawAPI.getSceneElements()).length > 0
      ) {
        framedOnOpenRef.current = true;
        seek(0);
      }
    };
    frameFirstScreen();
    return excalidrawAPI.onChange(frameFirstScreen);
  }, [open, excalidrawAPI, seek]);

  const stopPlayback = useCallback(() => {
    playingRef.current = false;
    setIsPlaying(false);
  }, []);

  const startPlayback = useCallback(() => {
    if (!excalidrawAPI || playingRef.current) {
      return;
    }
    playingRef.current = true;
    setIsPlaying(true);
    let last = performance.now();
    let t = playheadMs;
    const startElements = excalidrawAPI.getSceneElements();
    const total = buildSequence(
      getScreens(startElements),
      startElements,
    ).totalMs;
    if (t >= total - 1) {
      t = 0;
    }
    const step = (now: number) => {
      if (!playingRef.current) {
        return;
      }
      t += now - last;
      last = now;
      const nowElements = excalidrawAPI.getSceneElements();
      const totalNow = buildSequence(
        getScreens(nowElements),
        nowElements,
      ).totalMs;
      if (t >= totalNow) {
        if (loopRef.current) {
          t = 0;
        } else {
          t = totalNow;
          playingRef.current = false;
          setIsPlaying(false);
        }
      }
      setPlayheadMs(t);
      applyTime(t);
      if (playingRef.current) {
        requestAnimationFrame(step);
      }
    };
    requestAnimationFrame(step);
  }, [excalidrawAPI, playheadMs, applyTime]);

  // keyboard: space play/pause, ←/→ nudge (shift = 1s), Esc close
  useEffect(() => {
    if (!open) {
      return;
    }
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      if (
        target.tagName === "INPUT" ||
        target.tagName === "SELECT" ||
        target.tagName === "TEXTAREA" ||
        target.isContentEditable
      ) {
        return;
      }
      if (e.key === "Escape") {
        if (popover) {
          setPopover(null);
        } else if (!renamingId) {
          setOpen(false);
        }
        return;
      }
      if (e.code === "Space") {
        e.preventDefault();
        if (playingRef.current) {
          stopPlayback();
        } else {
          startPlayback();
        }
      }
      if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
        e.preventDefault();
        const delta =
          (e.key === "ArrowLeft" ? -1 : 1) * (e.shiftKey ? 1000 : 100);
        seek(Math.max(0, playheadMs + delta));
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [
    open,
    popover,
    renamingId,
    playheadMs,
    seek,
    startPlayback,
    stopPlayback,
  ]);

  if (!open || !excalidrawAPI) {
    return null;
  }

  const elements = excalidrawAPI.getSceneElements();
  const screens = getScreens(elements);
  const sequence = buildSequence(screens, elements);
  const xOf = (ms: number) => (ms / 1000) * PX_PER_SEC;

  const jumpTo = (frame: ExcalidrawFrameLikeElement) => {
    setActiveId(frame.id);
    excalidrawAPI.setViewport({
      target: [frame],
      fit: "scale-down",
      animation: true,
    });
  };

  const applyOrder = (fromIndex: number, toIndex: number) => {
    if (fromIndex === toIndex) {
      return;
    }
    for (const patch of screenOrderPatches(screens, fromIndex, toIndex)) {
      const el = elements.find((e) => e.id === patch.id);
      if (el) {
        excalidrawAPI.mutateElement(el, { customData: patch.customData });
      }
    }
    captureUndo();
  };

  const onDuplicate = (frame: ExcalidrawFrameLikeElement) => {
    const { newElements, newFrameId } = duplicateScreen(elements, frame);
    excalidrawAPI.updateScene({
      elements: [
        ...excalidrawAPI.getSceneElementsIncludingDeleted(),
        ...newElements,
      ],
      captureUpdate: CaptureUpdateAction.IMMEDIATELY,
    });
    const created = excalidrawAPI
      .getSceneElements()
      .find((el) => el.id === newFrameId) as
      | ExcalidrawFrameLikeElement
      | undefined;
    if (created) {
      jumpTo(created);
    }
  };

  const onDelete = (frame: ExcalidrawFrameLikeElement) => {
    const doomed = new Set(
      elements
        .filter((el) => el.id === frame.id || el.frameId === frame.id)
        .map((el) => el.id),
    );
    excalidrawAPI.updateScene({
      elements: excalidrawAPI
        .getSceneElementsIncludingDeleted()
        .map((el) =>
          doomed.has(el.id) ? { ...el, isDeleted: true } : el,
        ) as ExcalidrawElement[],
      captureUpdate: CaptureUpdateAction.IMMEDIATELY,
    });
  };

  const onAdd = () => {
    const last = screens[screens.length - 1];
    const frame = newFrameElement({
      x: last ? last.x + last.width + 80 : 0,
      y: last ? last.y : 0,
      width: last ? last.width : 800,
      height: last ? last.height : 600,
      name: `Screen ${screens.length + 1}`,
    });
    excalidrawAPI.updateScene({
      elements: [...excalidrawAPI.getSceneElementsIncludingDeleted(), frame],
      captureUpdate: CaptureUpdateAction.IMMEDIATELY,
    });
    jumpTo(frame as unknown as ExcalidrawFrameLikeElement);
  };

  const renameTo = (frame: ExcalidrawFrameLikeElement, name: string) => {
    excalidrawAPI.mutateElement(
      frame as ExcalidrawElement,
      {
        name: name.trim() || frame.name,
      } as never,
    );
    captureUndo();
    setRenamingId(null);
  };

  const frameById = (id: string) => screens.find((s) => s.id === id) ?? null;

  const setTransitionOn = (
    frame: ExcalidrawFrameLikeElement,
    patch: Parameters<typeof withScreenTransition>[1],
  ) => {
    excalidrawAPI.mutateElement(frame as ExcalidrawElement, {
      customData: withScreenTransition(frame, patch),
    });
    captureUndo();
  };

  const setCameraOn = (
    frame: ExcalidrawFrameLikeElement,
    patch: Parameters<typeof withScreenCamera>[1],
  ) => {
    excalidrawAPI.mutateElement(frame as ExcalidrawElement, {
      customData: withScreenCamera(frame, patch),
    });
    captureUndo();
  };

  const onTrimMove = (e: React.PointerEvent) => {
    const trim = trimRef.current;
    if (!trim) {
      return;
    }
    const deltaMs = ((e.clientX - trim.startX) / PX_PER_SEC) * 1000;
    const next = Math.min(
      60000,
      Math.max(100, Math.round((trim.startMs + deltaMs) / 50) * 50),
    );
    const el = elements.find((s) => s.id === trim.id);
    if (el) {
      excalidrawAPI.mutateElement(el, {
        customData: { ...el.customData, screenDurationMs: next },
      });
    }
  };

  const timeFromPointer = (clientX: number) => {
    const rect = tlContentRef.current?.getBoundingClientRect();
    if (!rect) {
      return 0;
    }
    return Math.max(
      0,
      Math.min(sequence.totalMs, ((clientX - rect.left) / PX_PER_SEC) * 1000),
    );
  };

  // Studio is self-sufficient for export: records the whole sequence right
  // here (same recordScreens path as the panel — transitions, cameras and
  // element animations included) and downloads the file.
  const runExport = async () => {
    if (exportProgress !== null || screens.length === 0) {
      return;
    }
    stopPlayback();
    releaseOverrides(); // the export runtime must own the override slot
    setExportProgress(0);
    try {
      const blob = await recordScreens({
        elements,
        appState: excalidrawAPI.getAppState(),
        files: excalidrawAPI.getFiles(),
        screens: screens.map((frame) => ({
          frame,
          durationMs: getScreenDurationMs(frame),
        })),
        format: exportFormat,
        fps: 24,
        scale: 2,
        loopForever: true,
        quality: "medium",
        onProgress: (done, total) =>
          setExportProgress(Math.round((done / total) * 100)),
      });
      const ext = exportFormat === "apng" ? "png" : exportFormat;
      downloadBlob(blob, `sequence.${ext}`);
    } catch (error) {
      console.error("[studio] export failed", error);
      window.alert(error instanceof Error ? error.message : String(error));
    } finally {
      setExportProgress(null);
    }
  };

  // one lane per animated element, bars laid on the sequence clock (chained
  // clips stack sequentially, same convention as the clip timeline)
  const lanes: {
    id: string;
    bars: { left: number; width: number; color: string; label: string }[];
  }[] = [];
  const elementsMapForLanes = arrayToMap(elements);
  for (const entry of sequence.entries) {
    const members = getElementsOverlappingFrame(
      elements,
      entry.frame,
      elementsMapForLanes,
    ).filter((el) => el.id !== entry.frame.id);
    // shared layout math (animation/layout.ts) — lanes, the clip timeline and
    // runtime.compile agree by construction, incl. "with" clips and
    // cross-element start offsets
    const { order, starts } = layoutElements(members);
    for (const member of order) {
      const memberStartMs = starts.get(member.id) ?? 0;
      const clips = getElementAnimations(member);
      const bars = layoutClips(clips).map((bar, i) => {
        const preset = animationPresets[clips[i].preset];
        return {
          left: xOf(entry.startMs + memberStartMs + bar.start),
          width: Math.max(6, xOf(bar.width)),
          color: CATEGORY_COLORS[preset?.category ?? "custom"] ?? "#9a9aa0",
          label: preset?.name ?? clips[i].preset,
        };
      });
      lanes.push({ id: `${entry.frame.id}:${member.id}`, bars });
    }
  }

  const seconds = Math.max(1, Math.ceil(sequence.totalMs / 1000));
  const popFrame = popover ? frameById(popover.frameId) : null;
  const popTransition = popFrame ? getScreenTransition(popFrame) : null;
  const popCamera = popFrame ? getScreenCamera(popFrame) : null;

  // focused stage: everything outside the active screen is masked off —
  // Studio shows ONE screen; customization lives on the chrome + timeline
  const stageFrame =
    screens.find((s) => s.id === activeId) ?? screens[0] ?? null;
  const stageRect = (() => {
    if (!stageFrame) {
      return null;
    }
    const st = excalidrawAPI.getAppState();
    const tl = sceneCoordsToViewportCoords(
      { sceneX: stageFrame.x, sceneY: stageFrame.y },
      st,
    );
    const br = sceneCoordsToViewportCoords(
      {
        sceneX: stageFrame.x + stageFrame.width,
        sceneY: stageFrame.y + stageFrame.height,
      },
      st,
    );
    return { x: tl.x, y: tl.y, w: br.x - tl.x, h: br.y - tl.y };
  })();

  return (
    <div className="studio" data-testid="studio">
      <div className="studio__stage" />
      {stageRect && (
        <>
          <div
            className="studio__mask"
            data-testid="studio-mask"
            style={{
              left: 0,
              top: 0,
              right: 0,
              height: Math.max(0, stageRect.y),
            }}
          />
          <div
            className="studio__mask"
            data-testid="studio-mask"
            style={{
              left: 0,
              top: stageRect.y,
              width: Math.max(0, stageRect.x),
              height: stageRect.h,
            }}
          />
          <div
            className="studio__mask"
            data-testid="studio-mask"
            style={{
              left: stageRect.x + stageRect.w,
              top: stageRect.y,
              right: 0,
              height: stageRect.h,
            }}
          />
          <div
            className="studio__mask"
            data-testid="studio-mask"
            style={{
              left: 0,
              top: stageRect.y + stageRect.h,
              right: 0,
              bottom: 0,
            }}
          />
          <div
            className="studio__bezel"
            style={{
              left: stageRect.x - 1,
              top: stageRect.y - 1,
              width: stageRect.w + 2,
              height: stageRect.h + 2,
            }}
          />
        </>
      )}

      <div className="studio__topbar">
        <span className="studio__title">◤ Studio</span>
        <span className="studio__meta">
          {screens.length} screen{screens.length === 1 ? "" : "s"}
        </span>
        <div className="studio__transport">
          <button
            className="studio__play"
            data-testid="studio-play"
            title="Play / pause (Space)"
            onClick={() => (isPlaying ? stopPlayback() : startPlayback())}
          >
            {isPlaying ? "⏸" : "▶︎"}
          </button>
          <button
            title="Stop and rewind"
            className="studio__top-btn"
            onClick={() => {
              stopPlayback();
              seek(0);
            }}
          >
            ⏹
          </button>
          <button
            className={`studio__top-btn${loop ? " studio__top-btn--on" : ""}`}
            title="Loop playback"
            onClick={() => setLoop(!loop)}
          >
            ⟲
          </button>
          <span className="studio__clock" data-testid="studio-clock">
            {fmtTime(playheadMs)} / {fmtTime(sequence.totalMs)}
          </span>
        </div>
        <select
          className="studio__format"
          title="Export format"
          value={exportFormat}
          onChange={(e) => setExportFormat(e.target.value as ExportFormat)}
        >
          <option value="gif">GIF</option>
          <option value="apng">APNG</option>
          <option value="mp4">MP4</option>
          <option value="webm">WebM</option>
        </select>
        <button
          className="studio__export"
          data-testid="studio-export"
          title="Export the whole sequence — transitions, cameras and animations included"
          disabled={exportProgress !== null}
          onClick={runExport}
        >
          {exportProgress !== null ? `${exportProgress}%` : "Export"}
        </button>
        <button
          className="studio__top-btn"
          title="Close Studio (Esc)"
          onClick={() => setOpen(false)}
        >
          ✕
        </button>
      </div>

      <div className="studio__rail" data-testid="studio-rail">
        {screens.map((frame, index) => (
          <div
            key={frame.id}
            className={`studio__card${
              activeId === frame.id ? " studio__card--active" : ""
            }${dropIndex === index ? " studio__card--drop" : ""}`}
            draggable={renamingId !== frame.id}
            onDragStart={() => {
              dragFrom.current = index;
            }}
            onDragOver={(e) => {
              e.preventDefault();
              setDropIndex(index);
            }}
            onDrop={(e) => {
              e.preventDefault();
              if (dragFrom.current !== null) {
                applyOrder(dragFrom.current, index);
              }
              dragFrom.current = null;
              setDropIndex(null);
            }}
            onDragEnd={() => {
              dragFrom.current = null;
              setDropIndex(null);
            }}
            onClick={() => jumpTo(frame)}
          >
            <ScreenThumb frame={frame} version={frame.versionNonce ?? 0} />
            <div className="studio__card-body">
              {renamingId === frame.id ? (
                <input
                  className="studio__rename"
                  autoFocus
                  defaultValue={frame.name ?? `Screen ${index + 1}`}
                  onClick={(e) => e.stopPropagation()}
                  onBlur={(e) => renameTo(frame, e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      renameTo(frame, (e.target as HTMLInputElement).value);
                    }
                    if (e.key === "Escape") {
                      setRenamingId(null);
                    }
                  }}
                />
              ) : (
                <span
                  className="studio__card-name"
                  title="Double-click to rename"
                  onDoubleClick={(e) => {
                    e.stopPropagation();
                    setRenamingId(frame.id);
                  }}
                >
                  {frame.name ?? `Screen ${index + 1}`}
                </span>
              )}
              <span className="studio__card-meta">
                {(getScreenDurationMs(frame) / 1000).toFixed(1)}s
                {getScreenTransition(frame) ? " · ◈" : ""}
                {getScreenCamera(frame) ? " · ⌁" : ""}
              </span>
            </div>
            <div className="studio__card-actions">
              <button
                title="Move up"
                disabled={index === 0}
                onClick={(e) => {
                  e.stopPropagation();
                  applyOrder(index, index - 1);
                }}
              >
                ↑
              </button>
              <button
                title="Move down"
                disabled={index === screens.length - 1}
                onClick={(e) => {
                  e.stopPropagation();
                  applyOrder(index, index + 1);
                }}
              >
                ↓
              </button>
              <button
                title="Camera move (zoom, pan, shake…)"
                onClick={(e) => {
                  e.stopPropagation();
                  setPopover({
                    kind: "camera",
                    frameId: frame.id,
                    x: e.clientX,
                  });
                }}
              >
                ⌁
              </button>
              {index > 0 && (
                <button
                  title="Transition into this screen"
                  onClick={(e) => {
                    e.stopPropagation();
                    setPopover({
                      kind: "joint",
                      frameId: frame.id,
                      x: e.clientX,
                    });
                  }}
                >
                  ◈
                </button>
              )}
              <button
                title="Duplicate screen"
                onClick={(e) => {
                  e.stopPropagation();
                  onDuplicate(frame);
                }}
              >
                ⧉
              </button>
              <button
                title="Delete screen"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(frame);
                }}
              >
                ✕
              </button>
            </div>
          </div>
        ))}
        <button
          className="studio__add"
          data-testid="studio-add"
          onClick={onAdd}
        >
          + Add screen
        </button>
      </div>

      <div className="studio__inspector" data-testid="studio-inspector">
        <div className="studio__inspector-title">Animate</div>
        {/* the full preset gallery + clip customization — select an element
            on the stage and everything applies right here */}
        <AnimationPanel />
      </div>

      <div className="studio__timeline" data-testid="studio-timeline">
        <div className="studio__tl-scroll">
          <div
            ref={tlContentRef}
            className="studio__tl-content"
            style={{ width: xOf(sequence.totalMs) + 48 }}
          >
            <div
              className="studio__ruler"
              data-testid="studio-ruler"
              onPointerDown={(e) => {
                (e.currentTarget as Element).setPointerCapture?.(e.pointerId);
                scrubbingRef.current = true;
                stopPlayback();
                seek(timeFromPointer(e.clientX));
              }}
              onPointerMove={(e) => {
                if (scrubbingRef.current) {
                  seek(timeFromPointer(e.clientX));
                }
              }}
              onPointerUp={() => {
                scrubbingRef.current = false;
              }}
            >
              {Array.from({ length: seconds + 1 }, (_, s) => (
                <span
                  key={s}
                  className="studio__tick"
                  style={{ left: s * PX_PER_SEC }}
                >
                  {s}s
                </span>
              ))}
            </div>
            <div className="studio__tl-track">
              {sequence.entries.map((entry, index) => (
                <div key={entry.frame.id} className="studio__tl-cell">
                  {index > 0 &&
                    (entry.transitionIn ? (
                      <button
                        className="studio__tl-joint"
                        data-testid="studio-joint"
                        title={`${entry.transitionIn.type} · ${entry.transitionIn.durationMs}ms — click to edit`}
                        style={{
                          width: Math.max(
                            14,
                            xOf(entry.transitionIn.durationMs),
                          ),
                        }}
                        onClick={(e) =>
                          setPopover({
                            kind: "joint",
                            frameId: entry.frame.id,
                            x: e.clientX,
                          })
                        }
                      >
                        ◈
                      </button>
                    ) : (
                      <button
                        className="studio__tl-joint studio__tl-joint--ghost"
                        data-testid="studio-joint-add"
                        title="Add a transition into this screen"
                        onClick={(e) =>
                          setPopover({
                            kind: "joint",
                            frameId: entry.frame.id,
                            x: e.clientX,
                          })
                        }
                      >
                        ＋
                      </button>
                    ))}
                  <div
                    className={`studio__tl-block${
                      activeId === entry.frame.id
                        ? " studio__tl-block--active"
                        : ""
                    }`}
                    style={{ width: xOf(entry.durationMs) }}
                    onClick={() => {
                      stopPlayback();
                      seek(entry.startMs);
                    }}
                    title={`${entry.frame.name ?? `Screen ${index + 1}`} · ${(
                      entry.durationMs / 1000
                    ).toFixed(1)}s`}
                  >
                    <span className="studio__tl-label">
                      {entry.frame.name ?? `Screen ${index + 1}`}
                    </span>
                    <button
                      className={`studio__tl-cam${
                        entry.camera ? " studio__tl-cam--on" : ""
                      }`}
                      data-testid="studio-cam"
                      title={
                        entry.camera
                          ? `Camera: ${
                              CAMERA_MOVE_LABELS[entry.camera.move]
                            } — click to edit`
                          : "Add a camera move"
                      }
                      onClick={(e) => {
                        e.stopPropagation();
                        setPopover({
                          kind: "camera",
                          frameId: entry.frame.id,
                          x: e.clientX,
                        });
                      }}
                    >
                      ⌁
                    </button>
                    <div
                      className="studio__tl-trim"
                      title="Drag to trim this screen's duration"
                      onPointerDown={(e) => {
                        e.stopPropagation();
                        (e.currentTarget as Element).setPointerCapture?.(
                          e.pointerId,
                        );
                        trimRef.current = {
                          id: entry.frame.id,
                          startX: e.clientX,
                          startMs: entry.durationMs,
                        };
                      }}
                      onPointerMove={onTrimMove}
                      onPointerUp={() => {
                        if (trimRef.current) {
                          trimRef.current = null;
                          captureUndo();
                        }
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
            {lanes.length > 0 && (
              <div className="studio__lanes" data-testid="studio-lanes">
                {lanes.slice(0, 10).map((lane) => (
                  <div key={lane.id} className="studio__lane">
                    {lane.bars.map((bar, i) => (
                      <span
                        key={i}
                        className="studio__lane-bar"
                        title={bar.label}
                        style={{
                          left: bar.left,
                          width: bar.width,
                          background: bar.color,
                        }}
                      />
                    ))}
                  </div>
                ))}
                {lanes.length > 10 && (
                  <span className="studio__lane-more">
                    +{lanes.length - 10} more
                  </span>
                )}
              </div>
            )}
            <div
              className="studio__playhead"
              data-testid="studio-playhead"
              style={{ left: xOf(playheadMs) }}
            />
          </div>
        </div>
      </div>

      {popover && popFrame && (
        <div
          className="studio__popover"
          data-testid="studio-popover"
          style={{
            left: Math.max(
              12,
              Math.min(window.innerWidth - 336, popover.x - 160),
            ),
          }}
        >
          {popover.kind === "joint" ? (
            <>
              <div className="studio__pop-title">
                Transition into “{popFrame.name ?? "screen"}”
              </div>
              <div className="studio__opt-group">
                <label className="studio__opt">
                  <span>Type</span>
                  <select
                    data-testid="studio-joint-type"
                    value={popTransition?.type ?? "cut"}
                    onChange={(e) =>
                      setTransitionOn(popFrame, {
                        type: e.target.value as TransitionType,
                        durationMs:
                          popTransition?.durationMs ?? DEFAULT_TRANSITION_MS,
                        direction: popTransition?.direction ?? 0,
                      })
                    }
                  >
                    {TRANSITION_TYPES.map((t) => (
                      <option key={t} value={t}>
                        {t[0].toUpperCase() + t.slice(1)}
                      </option>
                    ))}
                  </select>
                </label>
                {popTransition &&
                  (popTransition.type === "wipe" ||
                    popTransition.type === "push") && (
                    <label className="studio__opt">
                      <span>Direction</span>
                      <select
                        value={popTransition.direction ?? 0}
                        onChange={(e) =>
                          setTransitionOn(popFrame, {
                            ...popTransition,
                            direction: Number(e.target.value) as 0 | 1 | 2 | 3,
                          })
                        }
                      >
                        {["← Left", "→ Right", "↑ Up", "↓ Down"].map(
                          (label, i) => (
                            <option key={label} value={i}>
                              {label}
                            </option>
                          ),
                        )}
                      </select>
                    </label>
                  )}
                {popTransition && (
                  <label className="studio__opt">
                    <span>Duration</span>
                    <input
                      type="number"
                      min={100}
                      max={5000}
                      step={100}
                      value={popTransition.durationMs}
                      onChange={(e) => {
                        const value = Number(e.target.value);
                        if (Number.isFinite(value) && e.target.value !== "") {
                          setTransitionOn(popFrame, {
                            ...popTransition,
                            durationMs: value,
                          });
                        }
                      }}
                    />
                  </label>
                )}
              </div>
            </>
          ) : (
            <>
              <div className="studio__pop-title">
                Camera on “{popFrame.name ?? "screen"}”
              </div>
              <div className="studio__opt-group">
                <label className="studio__opt">
                  <span>Move</span>
                  <select
                    data-testid="studio-cam-move"
                    value={popCamera?.move ?? ""}
                    onChange={(e) =>
                      setCameraOn(
                        popFrame,
                        e.target.value
                          ? {
                              move: e.target
                                .value as typeof CAMERA_MOVES[number],
                              amount: popCamera?.amount ?? 0.25,
                              ...(popCamera?.keyframes
                                ? { keyframes: popCamera.keyframes }
                                : {}),
                            }
                          : null,
                      )
                    }
                  >
                    <option value="">No camera</option>
                    {CAMERA_MOVES.map((move) => (
                      <option key={move} value={move}>
                        {CAMERA_MOVE_LABELS[move]}
                      </option>
                    ))}
                  </select>
                </label>
                {popCamera && popCamera.move !== "keyframed" && (
                  <label className="studio__opt">
                    <span>Amount %</span>
                    <input
                      type="number"
                      min={5}
                      max={100}
                      step={5}
                      title="Amount (%)"
                      value={Math.round(popCamera.amount * 100)}
                      onChange={(e) => {
                        const value = Number(e.target.value);
                        if (Number.isFinite(value) && e.target.value !== "") {
                          setCameraOn(popFrame, {
                            ...popCamera,
                            amount: value / 100,
                          });
                        }
                      }}
                    />
                  </label>
                )}
              </div>
              {popCamera?.move === "keyframed" && (
                <KeyframeEditor
                  tracks={popCamera.keyframes ?? {}}
                  durationMs={getScreenDurationMs(popFrame)}
                  channels={CAMERA_TRACK_CHANNELS}
                  onChange={(keyframes) =>
                    setCameraOn(popFrame, { ...popCamera, keyframes })
                  }
                  onClose={() => setPopover(null)}
                />
              )}
            </>
          )}
          <button
            className="studio__pop-close"
            title="Close"
            onClick={() => setPopover(null)}
          >
            Done
          </button>
        </div>
      )}
    </div>
  );
};
